// src/components/VisorRA.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { IconButton } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import './VisorRA.css';

/* ----------------------------------------------------------
   Cargar @google/model-viewer una sola vez
----------------------------------------------------------- */
let modelViewerLoading = null;
const ensureModelViewer = () => {
  if (typeof window !== 'undefined' && customElements.get('model-viewer'))
    return Promise.resolve();

  if (!modelViewerLoading) {
    modelViewerLoading = import('@google/model-viewer/dist/model-viewer.min.js').then(async () => {
      try { await customElements.whenDefined('model-viewer'); } catch {}
    });
  }
  return modelViewerLoading;
};

/* ----------------------------------------------------------
   Utils
----------------------------------------------------------- */
const isUrl = (str) => {
  if (!str || typeof str !== 'string') return false;
  try { new URL(str); return true; } catch { return false; }
};
const toPosAttr = (p) =>
  `${Number(p?.x ?? 0).toFixed(4)} ${Number(p?.y ?? 0).toFixed(4)} ${Number(p?.z ?? 0).toFixed(4)}`;
const toNormAttr = (n) => {
  const nx = Number(n?.x ?? 0), ny = Number(n?.y ?? 1), nz = Number(n?.z ?? 0);
  const len = Math.hypot(nx, ny, nz) || 1;
  return `${(nx/len).toFixed(4)} ${(ny/len).toFixed(4)} ${(nz/len).toFixed(4)}`;
};

const CAMERA_PRESETS = {
  small: {
    cameraOrbit: '0deg 75deg auto',
    minCameraOrbit: 'auto auto 0.35m',
    maxCameraOrbit: 'auto auto 8m',
    fieldOfView: '42deg',
    minFieldOfView: '22deg',
    maxFieldOfView: '58deg',
  },
  large: {
    cameraOrbit: '0deg 75deg auto',
    minCameraOrbit: 'auto auto 1.5m',
    maxCameraOrbit: 'auto auto 60m',
    fieldOfView: '48deg',
    minFieldOfView: '28deg',
    maxFieldOfView: '62deg',
  },
};
const SMALL_MODEL_MAX_DIAGONAL = 2.5;
const getAutoPresetFromDimensions = (dim) => {
  if (!dim) return 'small';
  const diag = Math.hypot(Number(dim.x || 0), Number(dim.y || 0), Number(dim.z || 0));
  return diag <= SMALL_MODEL_MAX_DIAGONAL ? 'small' : 'large';
};
const resolvePresetKey = (modelSizePreset, dim) => {
  if (modelSizePreset === 'small' || modelSizePreset === 'large') return modelSizePreset;
  return getAutoPresetFromDimensions(dim);
};

/* ----------------------------------------------------------
   Utils Video
----------------------------------------------------------- */
const isYouTube = (u) => isUrl(u) && /(^|\.)youtube\.com$|(^|\.)youtu\.be$/.test(new URL(u).hostname);
const isVimeo   = (u) => isUrl(u) && /(^|\.)vimeo\.com$/.test(new URL(u).hostname);
const isHls     = (u) => typeof u === 'string' && u.toLowerCase().includes('.m3u8');
const isDirectVideo = (u) => typeof u === 'string' && /\.(mp4|webm|ogg)(\?|#|$)/i.test(u);

const getYouTubeEmbed = (u) => {
  try {
    const url = new URL(u);
    if (url.hostname.includes('youtu.be')) return `https://www.youtube.com/embed/${url.pathname.slice(1)}`;
    const v = url.searchParams.get('v');
    if (v) return `https://www.youtube.com/embed/${v}`;
    const segs = url.pathname.split('/').filter(Boolean);
    return `https://www.youtube.com/embed/${segs.pop()}`;
  } catch { return null; }
};
const getVimeoEmbed = (u) => {
  try {
    const url = new URL(u);
    const segs = url.pathname.split('/').filter(Boolean);
    return `https://player.vimeo.com/video/${segs.pop()}`;
  } catch { return null; }
};

/* ----------------------------------------------------------
   Cargar HLS cuando hace falta
----------------------------------------------------------- */
let hlsLoading = null;
const ensureHls = () => {
  if (window.Hls) return Promise.resolve();
  if (!hlsLoading) {
    hlsLoading = new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.src = 'https://cdn.jsdelivr.net/npm/hls.js@latest';
      s.onload = resolve;
      s.onerror = reject;
      document.head.appendChild(s);
    });
  }
  return hlsLoading;
};

/* ----------------------------------------------------------
   Subcomponente VideoPlayer
----------------------------------------------------------- */
const VideoPlayer = ({ src, title = 'Video' }) => {
  const videoRef = useRef(null);
  const kind = useMemo(() => {
    if (isYouTube(src)) return 'youtube';
    if (isVimeo(src)) return 'vimeo';
    if (isHls(src)) return 'hls';
    if (isDirectVideo(src)) return 'file';
    return 'unknown';
  }, [src]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || kind === 'youtube' || kind === 'vimeo') return;

    const attach = async () => {
      if (kind === 'hls') {
        if (video.canPlayType('application/vnd.apple.mpegurl')) {
          video.src = src;
          return;
        }
        try {
          await ensureHls();
          if (window.Hls && window.Hls.isSupported()) {
            const hls = new window.Hls({ enableWorker: true });
            hls.loadSource(src);
            hls.attachMedia(video);
          } else video.src = src;
        } catch { video.src = src; }
      } else {
        video.src = src;
      }
    };

    attach();
  }, [src, kind]);

  if (kind === 'youtube')
    return (
      <div className="hotspot-panel-media" style={{ position: 'relative', paddingBottom: '56.25%', height: 0 }}>
        <iframe
          title={title}
          src={getYouTubeEmbed(src)}
          allow="autoplay; fullscreen; picture-in-picture; encrypted-media"
          allowFullScreen
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', borderRadius: 10 }}
          frameBorder="0"
        />
      </div>
    );

  if (kind === 'vimeo')
    return (
      <div className="hotspot-panel-media" style={{ position: 'relative', paddingBottom: '56.25%', height: 0 }}>
        <iframe
          title={title}
          src={getVimeoEmbed(src)}
          allow="autoplay; fullscreen; picture-in-picture; encrypted-media"
          allowFullScreen
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', borderRadius: 10 }}
          frameBorder="0"
        />
      </div>
    );

  return (
    <video
      ref={videoRef}
      className="hotspot-panel-media"
      controls
      playsInline
      crossOrigin="anonymous"
      preload="metadata"
      controlsList="nodownload"
      style={{ width: '100%', maxHeight: '50vh', marginTop: 12, borderRadius: 10, background: '#000' }}
    />
  );
};

/* ----------------------------------------------------------
   Componente principal VisorRA
----------------------------------------------------------- */
const VisorRA = ({
  initialModelUrl,
  onClose,
  isMobile,
  publicMode = false,
  anchors = [],
  anchorsLoading = false,
  modelSizePreset = 'auto',
  autoEnterAr = false,
  onArStatusChange,
  onModelTransformChange,
  onCameraOrbitChange,
  initialScale = null,
  initialRotationY = null,
  initialArTransform = null,
  persistModelTransform = true,
}) => {
  const [modelUrl, setModelUrl] = useState(initialModelUrl || '');
  const [mvReady, setMvReady] = useState(false);
  const [mvLoading, setMvLoading] = useState(true);
  const [mvProgress, setMvProgress] = useState(0);
  const [mvError, setMvError] = useState(null);
  const [activeHotspot, setActiveHotspot] = useState(null);
  const [showHints, setShowHints] = useState(true);
  const [availableAnimations, setAvailableAnimations] = useState([]);
  const [selectedAnimation, setSelectedAnimation] = useState('');
  const [isAnimationPlaying, setIsAnimationPlaying] = useState(false);
  const [arSessionActive, setArSessionActive] = useState(false);
  const [cameraPresetKey, setCameraPresetKey] = useState(
    modelSizePreset === 'large' ? 'large' : 'small'
  );
  const mvRef = useRef(null);
  const localObjectUrlRef = useRef(null);
  const autoArAttemptedRef = useRef(false);

  /* Persistencia de transform por modelo */
  const modelTransformKey = useMemo(() => `modelTransform_${modelUrl}`, [modelUrl]);
  const saveModelTransform = useCallback((t) => {
    if (!persistModelTransform) return;
    try { localStorage.setItem(modelTransformKey, JSON.stringify(t)); } catch {}
  }, [modelTransformKey, persistModelTransform]);
  const getSavedModelTransform = useCallback(() => {
    if (!persistModelTransform) return null;
    try {
      const t = localStorage.getItem(modelTransformKey);
      return t ? JSON.parse(t) : null;
    } catch { return null; }
  }, [modelTransformKey, persistModelTransform]);
  const animationPrefKey = useMemo(() => `modelAnimation_${modelUrl}`, [modelUrl]);

  /* Carga de model-viewer */
  useEffect(() => {
    let alive = true;
    ensureModelViewer().then(() => alive && setMvReady(true));
    return () => { alive = false; };
  }, []);

  /* Reset al cambiar modelo */
  useEffect(() => {
    setMvLoading(true);
    setMvProgress(0);
    setMvError(null);
    setActiveHotspot(null);
    setShowHints(true);
    setAvailableAnimations([]);
    setSelectedAnimation('');
    setIsAnimationPlaying(false);
    setArSessionActive(false);
    autoArAttemptedRef.current = false;
    setCameraPresetKey(modelSizePreset === 'large' ? 'large' : 'small');
  }, [modelUrl, modelSizePreset]);

  useEffect(() => {
    if (modelSizePreset === 'small' || modelSizePreset === 'large') {
      setCameraPresetKey(modelSizePreset);
    }
  }, [modelSizePreset]);

  useEffect(() => {
    const el = mvRef.current;
    if (!el) return;
    const parsedScale = Number(initialScale);
    if (Number.isFinite(parsedScale) && parsedScale > 0) {
      try { el.scale = `${parsedScale} ${parsedScale} ${parsedScale}`; } catch {}
    }
  }, [initialScale]);

  useEffect(() => {
    const el = mvRef.current;
    if (!el) return;
    const parsedRotation = Number(initialRotationY);
    if (Number.isFinite(parsedRotation)) {
      try { el.orientation = `0deg ${parsedRotation}deg 0deg`; } catch {}
    }
  }, [initialRotationY]);

  useEffect(() => {
    return () => {
      if (localObjectUrlRef.current) {
        URL.revokeObjectURL(localObjectUrlRef.current);
      }
    };
  }, []);

  /* -----------------------------------------
     🔥 AUTOESCALA DEL MODELO EN RA + AJUSTE ALTURA
  ------------------------------------------ */
  const autoScaleAndGroundModel = () => {
    const el = mvRef.current;
    if (!el) return;

    try {
      const box = el.getBoundingBox();
      const maxDim = Math.max(box.x, box.y, box.z);

      // tamaño objetivo de la escena
      const targetSize = 30;
      const factor = targetSize / maxDim;

      // aplicar escala uniforme
      el.scale = `${factor} ${factor} ${factor}`;

      // corregir altura para que la base toque el piso
      const baseY = box.min.y;
      const correction = -baseY * factor;

      el.position = `0 ${correction} 0`;

    } catch (e) {
      console.warn("No se pudo autoajustar modelo:", e);
    }
  };

  const forceDoubleSidedMaterials = useCallback((el) => {
    try {
      const materials = el?.model?.materials;
      if (!Array.isArray(materials)) return;
      materials.forEach((mat) => {
        try {
          if (typeof mat?.setDoubleSided === 'function') {
            mat.setDoubleSided(true);
            return;
          }
          if (Object.prototype.hasOwnProperty.call(mat || {}, 'doubleSided')) {
            mat.doubleSided = true;
          }
        } catch {}
      });
    } catch {}
  }, []);

  /* Eventos carga + AR */
  useEffect(() => {
    const el = mvRef.current;
    if (!el) return;

    const onLoad = () => {
      setMvLoading(false);
      setMvProgress(100);
      setMvError(null);
      try {
        const dimensions = el.getDimensions?.();
        const nextPresetKey = resolvePresetKey(modelSizePreset, dimensions);
        const nextPreset = CAMERA_PRESETS[nextPresetKey] || CAMERA_PRESETS.small;
        setCameraPresetKey(nextPresetKey);
        // Reencuadra al cargar para evitar que el modelo arranque demasiado lejos.
        el.cameraOrbit = nextPreset.cameraOrbit;
        el.minCameraOrbit = nextPreset.minCameraOrbit;
        el.maxCameraOrbit = nextPreset.maxCameraOrbit;
        el.fieldOfView = nextPreset.fieldOfView;
        el.minFieldOfView = nextPreset.minFieldOfView;
        el.maxFieldOfView = nextPreset.maxFieldOfView;
        el.jumpCameraToGoal?.();
        if (onCameraOrbitChange) {
          try {
            const orbit = el.getCameraOrbit?.();
            if (orbit) onCameraOrbitChange({ theta: orbit.theta, phi: orbit.phi, radius: orbit.radius });
          } catch {}
        }
        const parsedScale = Number(initialScale);
        if (Number.isFinite(parsedScale) && parsedScale > 0) {
          el.scale = `${parsedScale} ${parsedScale} ${parsedScale}`;
        }
        const parsedRotation = Number(initialRotationY);
        if (Number.isFinite(parsedRotation)) {
          el.orientation = `0deg ${parsedRotation}deg 0deg`;
        }
        onModelTransformChange?.({
          position: el.position,
          orientation: el.orientation,
          scale: el.scale
        });

        const animationList = Array.isArray(el.availableAnimations)
          ? el.availableAnimations.filter(Boolean)
          : [];
        setAvailableAnimations(animationList);
        // Fuerza doble cara para evitar desaparición de superficies al rotar en AR.
        forceDoubleSidedMaterials(el);
        if (animationList.length > 0) {
          let nextAnimation = animationList[0];
          try {
            const savedAnimation = localStorage.getItem(animationPrefKey);
            if (savedAnimation && animationList.includes(savedAnimation)) {
              nextAnimation = savedAnimation;
            }
          } catch {}
          setSelectedAnimation(nextAnimation);
          el.animationName = nextAnimation;
          el.pause?.();
          setIsAnimationPlaying(false);
        }
        if (autoEnterAr && isMobile && !autoArAttemptedRef.current) {
          autoArAttemptedRef.current = true;
          setTimeout(() => {
            try {
              const maybe = el.activateAR?.();
              if (maybe?.catch) maybe.catch(() => onArStatusChange?.('failed'));
            } catch {
              onArStatusChange?.('failed');
            }
          }, 350);
        }
      } catch {}
    };

    const onError = (err) => {
      setMvLoading(false);
      setMvError(err?.detail?.message || "Error al cargar modelo");
    };

    const onProgress = (ev) => {
      const p = Math.round((ev?.detail?.totalProgress || 0) * 100);
      if (!Number.isNaN(p)) setMvProgress(Math.max(0, Math.min(100, p)));
    };

    /* Tracking persistente */
    const onTracking = (ev) => {
      if (ev.detail.status === 'tracking') {
        const t = {
          position: el.position,
          orientation: el.orientation,
          scale: el.scale,
        };
        saveModelTransform(t);
        onModelTransformChange?.(t);
      }
    };

    /* AL INICIAR RA */
    const onARStart = (ev) => {
      const status = ev?.detail?.status;
      if (status) onArStatusChange?.(status);
      if (status === 'session-started') {
        setArSessionActive(true);
        const parsedInitialScale = Number(initialScale);
        const parsedInitialRotation = Number(initialRotationY);
        try {
          // Evita autoplay inesperado en sesión AR cuando el runtime lo permita.
          el.pause?.();
          setIsAnimationPlaying(false);
        } catch {}
        // Reaplica doble cara al entrar en AR por compatibilidad con runtimes inmersivos.
        forceDoubleSidedMaterials(el);

        // restaurar transform
        const nextTransform = initialArTransform || getSavedModelTransform();
        const hasTransformScale = typeof nextTransform?.scale === 'string' && nextTransform.scale.trim().length > 0;
        const hasExplicitScale = hasTransformScale || (Number.isFinite(parsedInitialScale) && parsedInitialScale > 0);
        if (!hasExplicitScale) {
          // Solo autoescala si no hay una escala configurada por admin/calibración.
          autoScaleAndGroundModel();
        }
        if (nextTransform) {
          try {
            if (nextTransform.position) el.position = nextTransform.position;
            if (nextTransform.orientation) el.orientation = nextTransform.orientation;
            if (nextTransform.scale) el.scale = nextTransform.scale;
          } catch {}
        } else {
          try {
            if (Number.isFinite(parsedInitialScale) && parsedInitialScale > 0) {
              el.scale = `${parsedInitialScale} ${parsedInitialScale} ${parsedInitialScale}`;
            }
            if (Number.isFinite(parsedInitialRotation)) {
              el.orientation = `0deg ${parsedInitialRotation}deg 0deg`;
            }
          } catch {}
        }
        onModelTransformChange?.({
          position: el.position,
          orientation: el.orientation,
          scale: el.scale,
        });
      }
      if (status === 'session-ended' || status === 'not-presenting') {
        setArSessionActive(false);
      }
    };

    const onPlay = () => setIsAnimationPlaying(true);
    const onPause = () => setIsAnimationPlaying(false);

    let orbitRaf = null;
    const onCameraChange = () => {
      if (!onCameraOrbitChange) return;
      if (orbitRaf) cancelAnimationFrame(orbitRaf);
      orbitRaf = requestAnimationFrame(() => {
        try {
          const orbit = el.getCameraOrbit?.();
          if (orbit) onCameraOrbitChange({ theta: orbit.theta, phi: orbit.phi, radius: orbit.radius });
        } catch {}
      });
    };

    el.addEventListener('load', onLoad);
    el.addEventListener('error', onError);
    el.addEventListener('progress', onProgress);
    el.addEventListener('ar-tracking', onTracking);
    el.addEventListener('ar-status', onARStart);
    el.addEventListener('play', onPlay);
    el.addEventListener('pause', onPause);
    el.addEventListener('camera-change', onCameraChange);

    return () => {
      el.removeEventListener('load', onLoad);
      el.removeEventListener('error', onError);
      el.removeEventListener('progress', onProgress);
      el.removeEventListener('ar-tracking', onTracking);
      el.removeEventListener('ar-status', onARStart);
      el.removeEventListener('play', onPlay);
      el.removeEventListener('pause', onPause);
      el.removeEventListener('camera-change', onCameraChange);
      if (orbitRaf) cancelAnimationFrame(orbitRaf);
    };
  }, [
    mvReady,
    modelUrl,
    animationPrefKey,
    autoEnterAr,
    getSavedModelTransform,
    initialArTransform,
    initialRotationY,
    initialScale,
    isMobile,
    modelSizePreset,
    onArStatusChange,
    onModelTransformChange,
    onCameraOrbitChange,
    forceDoubleSidedMaterials,
    saveModelTransform
  ]);

  useEffect(() => {
    const el = mvRef.current;
    if (!el || !selectedAnimation) return;
    try {
      el.animationName = selectedAnimation;
      localStorage.setItem(animationPrefKey, selectedAnimation);
    } catch {}
  }, [selectedAnimation, animationPrefKey]);

  useEffect(() => {
    const onKeyDown = (ev) => {
      if (ev.key !== 'Escape') return;
      if (activeHotspot?.anchor) setActiveHotspot(null);
      else if (onClose) onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [activeHotspot, onClose]);

  /* File local */
  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (localObjectUrlRef.current) URL.revokeObjectURL(localObjectUrlRef.current);
    const nextUrl = URL.createObjectURL(f);
    localObjectUrlRef.current = nextUrl;
    setModelUrl(nextUrl);
  };

  /* Hotspots */
  const handleHotspotClick = (a, ev) => {
    ev?.stopPropagation?.();
    if (a.type === 'link') {
      const href = isUrl(a.mediaUrl) ? a.mediaUrl : isUrl(a.text) ? a.text : null;
      if (href) return window.open(href, '_blank', 'noopener,noreferrer');
    }
    setActiveHotspot({ anchor: a });
  };

  const activeText = activeHotspot?.anchor?.text || activeHotspot?.anchor?.description || '';
  const hasText = activeText.trim().length > 0;

  const visibleAnchors = useMemo(
    () => anchors.filter(a => a?.visible !== false && a?.position),
    [anchors]
  );
  const cameraPreset = CAMERA_PRESETS[cameraPresetKey] || CAMERA_PRESETS.small;
  const hasModel = Boolean(modelUrl);
  const hasAnimations = availableAnimations.length > 0;
  const hintText = isMobile
    ? 'Mueve el modelo con un dedo y pellizca para acercar. Toca los puntos para ver detalles.'
    : 'Arrastra para rotar, usa la rueda para zoom y haz click en los puntos para ver detalles.';
  const handlePlayAnimation = () => {
    const el = mvRef.current;
    if (!el || !hasAnimations) return;
    try {
      if (selectedAnimation) el.animationName = selectedAnimation;
      el.play?.();
      setIsAnimationPlaying(true);
    } catch {}
  };
  const handlePauseAnimation = () => {
    const el = mvRef.current;
    if (!el || !hasAnimations) return;
    try {
      el.pause?.();
      setIsAnimationPlaying(false);
    } catch {}
  };
  const handleResetAnimation = () => {
    const el = mvRef.current;
    if (!el || !hasAnimations) return;
    try {
      if (selectedAnimation) el.animationName = selectedAnimation;
      el.currentTime = 0;
      el.pause?.();
      setIsAnimationPlaying(false);
    } catch {}
  };

  /* ----------------------------------------------------------
     Render
  ----------------------------------------------------------- */
  return (
    <div className="visor-container">
      
      {/* Botón cerrar */}
      {onClose && (
        <IconButton
          onClick={onClose}
          className="close-button"
          style={{ position: 'absolute', top: 10, right: 10, zIndex: 1000, color: 'white' }}
        >
          <CloseIcon />
        </IconButton>
      )}

      {/* Loading overlay */}
      {(mvLoading || anchorsLoading) && hasModel && (
        <div className="loading-overlay">
          <div className="loading-spinner" />
          <p>{mvLoading ? `Cargando modelo... ${mvProgress}%` : 'Cargando etiquetas...'}</p>
        </div>
      )}

      {/* Error */}
      {mvError && (
        <div className="error-message">
          <p>{mvError}</p>
          {!publicMode && (
            <div className="upload-options">
              <input
                id="model-upload"
                type="file"
                accept=".glb,.gltf,.usdz"
                onChange={handleFileChange}
                style={{ display: 'none' }}
              />
              <label htmlFor="model-upload" className="upload-button">Cargar modelo local</label>
            </div>
          )}
        </div>
      )}

      {!hasModel && !mvError && (
        <div className="error-message">
          <p>No hay un modelo cargado para visualizar.</p>
          {!publicMode && (
            <div className="upload-options">
              <input
                id="model-upload-empty"
                type="file"
                accept=".glb,.gltf,.usdz"
                onChange={handleFileChange}
                style={{ display: 'none' }}
              />
              <label htmlFor="model-upload-empty" className="upload-button">Cargar modelo local</label>
            </div>
          )}
        </div>
      )}

      {/* MODEL VIEWER */}
      {mvReady && modelUrl && (
        <model-viewer
          key={modelUrl}
          ref={mvRef}
          src={modelUrl}
          alt="Modelo 3D"
          ar
          ar-placement="floor"             // ⭐️ Detección de superficie plana
          ar-modes="webxr scene-viewer quick-look"
          camera-controls
          camera-orbit={cameraPreset.cameraOrbit}
          min-camera-orbit={cameraPreset.minCameraOrbit}
          max-camera-orbit={cameraPreset.maxCameraOrbit}
          camera-target="auto auto auto"
          field-of-view={cameraPreset.fieldOfView}
          min-field-of-view={cameraPreset.minFieldOfView}
          max-field-of-view={cameraPreset.maxFieldOfView}
          environment-image="neutral"
          exposure="1.0"
          crossorigin="anonymous"
          reveal="auto"
          interaction-prompt="auto"
          onPointerDown={() => setShowHints(false)}
          style={{ width: '100%', height: '100%', background: '#000' }}
        >
          {visibleAnchors.map((a) => (
            <button
              key={`hotspot-${a.id}`}
              slot={`hotspot-${a.id}`}
              onClick={(e) => handleHotspotClick(a, e)}
              className={[
                'hotspot',
                'saved',
                a.type === 'link'  ? 'is-link'  : '',
                a.type === 'image' ? 'is-image' : '',
                a.type === 'audio' ? 'is-audio' : '',
                a.type === 'video' ? 'is-video' : '',
              ].join(' ').trim()}
              data-position={toPosAttr(a.position)}
              data-normal={a.normal ? toNormAttr(a.normal) : '0 1 0'}
              title={a.title || 'Accesorio'}
              aria-label={`Abrir hotspot ${a.title || 'Accesorio'}`}
            >
              <span className="hotspot-dot" />
              <span className="hotspot-label">{a.title || 'Accesorio'}</span>
            </button>
          ))}
        </model-viewer>
      )}

      {hasModel && !mvLoading && !mvError && showHints && !activeHotspot?.anchor && (
        <div className="viewer-hints" onClick={() => setShowHints(false)}>
          <div className="viewer-hints-title">Guia rapida</div>
          <div className="viewer-hints-text">{hintText}</div>
          {visibleAnchors.length > 0 && (
            <div className="viewer-hints-chip">
              {visibleAnchors.length} punto{visibleAnchors.length !== 1 ? 's' : ''} interactivo{visibleAnchors.length !== 1 ? 's' : ''}
            </div>
          )}
          <div className="viewer-hints-dismiss">Toca para cerrar</div>
        </div>
      )}

      {hasModel && !mvLoading && !mvError && hasAnimations && (
        <div className="animation-hud" role="region" aria-label="Controles de animacion">
          <div className="animation-hud-title">Animacion del modelo</div>
          <div className="animation-hud-row">
            <select
              value={selectedAnimation}
              onChange={(e) => setSelectedAnimation(e.target.value)}
              className="animation-hud-select"
              aria-label="Seleccionar animacion"
            >
              {availableAnimations.map((animName) => (
                <option key={animName} value={animName}>{animName}</option>
              ))}
            </select>
            <div className="animation-hud-actions">
              {isAnimationPlaying ? (
                <button className="animation-hud-btn" onClick={handlePauseAnimation}>
                  Pausar
                </button>
              ) : (
                <button className="animation-hud-btn animation-hud-btn-primary" onClick={handlePlayAnimation}>
                  Reproducir
                </button>
              )}
              <button className="animation-hud-btn" onClick={handleResetAnimation}>
                Reiniciar
              </button>
            </div>
          </div>
          {arSessionActive && (
            <div className="animation-hud-note">
              En AR nativo la reproduccion automatica puede variar segun dispositivo.
            </div>
          )}
        </div>
      )}

      {/* PANEL HOTSPOT */}
      {activeHotspot?.anchor && (
        <>
          <div className="hotspot-backdrop" onClick={() => setActiveHotspot(null)} />
          <div className="hotspot-panel">

            <div className="hotspot-panel-header">
              <div className="hotspot-panel-title">
                {activeHotspot.anchor.title || 'Detalle'}
              </div>
              <button
                className="hotspot-panel-close"
                onClick={() => setActiveHotspot(null)}
                aria-label="Cerrar panel del hotspot"
              >
                ✕
              </button>
            </div>

            <div className="hotspot-panel-body">

              <div className="hotspot-panel-text">
                {hasText ? activeText : <em>Sin descripción.</em>}
              </div>

              {activeHotspot.anchor.type === 'image' &&
                isUrl(activeHotspot.anchor.mediaUrl) && (
                <img
                  src={activeHotspot.anchor.mediaUrl}
                  alt={activeHotspot.anchor.title}
                  className="hotspot-panel-media"
                />
              )}

              {activeHotspot.anchor.type === 'audio' &&
                isUrl(activeHotspot.anchor.mediaUrl) && (
                <audio src={activeHotspot.anchor.mediaUrl} controls className="hotspot-panel-media" />
              )}

              {activeHotspot.anchor.type === 'video' &&
                isUrl(activeHotspot.anchor.mediaUrl) && (
                <VideoPlayer
                  src={activeHotspot.anchor.mediaUrl}
                  title={activeHotspot.anchor.title || 'Video'}
                />
              )}

              {activeHotspot.anchor.type === 'link' && (
                <div style={{ marginTop: 12 }}>
                  {isUrl(activeHotspot.anchor.text) ? (
                    <a href={activeHotspot.anchor.text} target="_blank" rel="noreferrer">Abrir enlace</a>
                  ) : isUrl(activeHotspot.anchor.mediaUrl) ? (
                    <a href={activeHotspot.anchor.mediaUrl} target="_blank" rel="noreferrer">Abrir enlace</a>
                  ) : (
                    <span style={{ opacity: 0.8 }}>Sin URL válida</span>
                  )}
                </div>
              )}

            </div>
          </div>
        </>
      )}

    </div>
  );
};

export default VisorRA;
